import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getCheckoutStatus, getQuote, getWhatsAppLink } from "@/lib/api";
import { 
  Truck, 
  CheckCircle2, 
  Loader2,
  ArrowRight,
  PartyPopper
} from "lucide-react";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const quoteId = searchParams.get("quote_id");
  
  const [status, setStatus] = useState("checking");
  const [quote, setQuote] = useState(null);
  const [whatsappUrl, setWhatsappUrl] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      try {
        const statusResponse = await getCheckoutStatus(sessionId);
        
        if (statusResponse.payment_status === "paid") {
          setStatus("success");
          
          // Fetch quote details
          if (quoteId) {
            const quoteData = await getQuote(quoteId);
            setQuote(quoteData);
            
            // Track payment completion in Google Tag
            if (window.trackPaymentComplete) {
              window.trackPaymentComplete(
                quoteData?.quoted_price || quoteData?.paid_amount_gbp || 0,
                quoteData?.currency || 'GBP',
                quoteId
              );
            }
            
            // Get WhatsApp link
            try {
              const whatsappData = await getWhatsAppLink(quoteId);
              setWhatsappUrl(whatsappData.whatsapp_url);
            } catch (e) {
              console.log("WhatsApp not available yet");
            }
          }
        } else if (pollCount < 5) {
          // Continue polling
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus("pending");
        }
      } catch (error) {
        console.error("Error checking payment:", error);
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, quoteId, pollCount]);

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#22c55e] rounded-sm flex items-center justify-center">
              <Truck className="w-6 h-6 text-[#020617]" />
            </div>
            <div className="flex flex-col">
              <span className="font-['Barlow_Condensed'] text-xl font-bold text-white leading-tight">
                RTS EXPRESS
              </span>
              <span className="text-[10px] text-slate-400 tracking-wide">SAME DAY COURIER</span>
            </div>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-6 py-16">
        {/* Checking Status */}
        {status === "checking" && (
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-[#22c55e] mx-auto mb-6 animate-spin" />
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white mb-2">
              VERIFYING PAYMENT...
            </h1>
            <p className="text-slate-400">Please wait while we confirm your payment.</p>
          </div>
        )}

        {/* Success */}
        {status === "success" && (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-[#22c55e]/10 rounded-full mb-6 animate-pulse-glow">
                <CheckCircle2 className="w-12 h-12 text-[#22c55e]" />
              </div>
              <div className="flex items-center justify-center gap-2 mb-4">
                <PartyPopper className="w-6 h-6 text-[#f59e0b]" />
                <h1 className="font-['Barlow_Condensed'] text-4xl font-bold text-white">
                  PAYMENT <span className="text-[#22c55e]">CONFIRMED!</span>
                </h1>
                <PartyPopper className="w-6 h-6 text-[#f59e0b] scale-x-[-1]" />
              </div>
              <p className="text-slate-400 text-lg">
                Your driver has been dispatched!
              </p>
            </div>

            {/* Confirmation Details */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 mb-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#22c55e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Truck className="w-8 h-8 text-[#22c55e]" />
                </div>
                <p className="font-['Barlow_Condensed'] text-2xl font-bold text-white mb-2">
                  DRIVER ON THE WAY
                </p>
                <p className="text-slate-400 text-sm">
                  Our driver will arrive at the pickup location as scheduled.
                </p>
              </div>
            </div>

            {quote && (
              <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 mb-6">
                <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4">
                  Booking Details
                </h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Reference:</span>
                    <span className="text-white font-mono">{quoteId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Pickup:</span>
                    <span className="text-white">{quote.pickup_postcode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Delivery:</span>
                    <span className="text-white">{quote.delivery_postcode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Amount Paid:</span>
                    <span className="text-[#22c55e] font-bold">£{quote.quoted_price?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* WhatsApp Button */}
            {whatsappUrl && (
              <div className="bg-[#25D366]/10 border border-[#25D366]/20 rounded-sm p-6 mb-6 text-center">
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-2">
                  Contact RTS Dispatch
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Our dedicated dispatch team is available 24/7 to assist you with your shipment.
                </p>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="bg-[#25D366] hover:bg-[#25D366]/90 text-white font-semibold"
                    data-testid="whatsapp-btn"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Contact RTS Dispatch
                  </Button>
                </a>
                <p className="text-slate-500 text-xs mt-3">Fast response • Real-time updates • Professional support</p>
              </div>
            )}

            {/* Email Notification */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-sm p-4 mb-8">
              <p className="text-slate-400 text-sm text-center">
                A confirmation email has been sent to <strong className="text-white">{quote?.email}</strong>
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/quote/${quoteId}`}>
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                >
                  View Full Details
                </Button>
              </Link>
              <Link to="/">
                <Button className="w-full sm:w-auto bg-[#22c55e] text-[#020617] hover:bg-[#22c55e]/90 font-semibold">
                  Back to Home
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </>
        )}

        {/* Pending */}
        {status === "pending" && (
          <div className="text-center">
            <div className="w-20 h-20 bg-[#f59e0b]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#f59e0b]" />
            </div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white mb-2">
              PAYMENT PROCESSING
            </h1>
            <p className="text-slate-400 mb-6">
              Your payment is being processed. Please wait a moment.
            </p>
            <Button 
              onClick={() => setPollCount(0)}
              className="bg-[#22c55e] text-[#020617] hover:bg-[#22c55e]/90"
            >
              Check Again
            </Button>
          </div>
        )}

        {/* Error */}
        {status === "error" && (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">!</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white mb-2">
              PAYMENT ERROR
            </h1>
            <p className="text-slate-400 mb-6">
              We couldn't verify your payment. Please try again or contact support.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/">
                <Button variant="outline" className="border-slate-700 text-slate-400 hover:bg-slate-800">
                  Back to Home
                </Button>
              </Link>
              {quoteId && (
                <Link to={`/quote/${quoteId}`}>
                  <Button className="bg-[#22c55e] text-[#020617] hover:bg-[#22c55e]/90">
                    Try Again
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
